import { Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
  ElementRef, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { WorkspaceService } from '../workspace.service';
import { GlobalService } from '../../global/services/global.service';
import { AppService } from '../../app.service';
import { DataFiltersService, FilterObj } from './data-filters.service';
import { ScreenDocumentsComponent } from '../../screen-docs/screen-documents.component';
import { ComponentRegistryService } from '../../core/grid/services/component-registry.service';
import { CeHostDialogComponent, CeHostDialogData } from '../../shared/components/ce-host-dialog/ce-host-dialog.component';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { GridEventService } from '../../core/grid/services/grid-event.service';
import { TabGridLoaderService } from '../../core/grid/services/tab-grid-loader.service';
import { environment } from '../../../environments/environment';

import { safeJsonInput } from '../../shared/_utils/safe-json-input';
const IMPORT_SCREEN_TYPES = ['grid-import', 'grid-import-td', 'grid-import-pivot'];

/**
 * Screens where changing tax year / scenario / jcd from the global header or
 * from the filter bar's `tax_year` control must NOT wipe `activeScreen.filters`.
 *
 * Default behaviour (all other screens): null `activeScreen.filters` and
 * re-run `/getFilters` so every dependent dropdown is rebuilt for the new year
 * — correct when selections are year-scoped (ME lists, filing groups, etc.).
 *
 * DST / workspace audit (gtw-elements):
 * - Workspace shell (`workspace.component.html`) mounts `<gtw-data-filters>`.
 *   Only screens with a non-null `filter_key` in USER_MENU load real filter
 *   definitions from `/getFilters`; changing tax year then hits
 *   `syncTaxYearToGlobalAndReload` + `globalParamsChanged$`, which null
 *   `activeScreen.filters` by design — that is the "everything reset" bug.
 * - Bundled `navigation-json.json`: menu entry **DST** is `workspace.dst`,
 *   `screen_key` **10608**, **`filter_key`: null** — the strip uses a synthetic
 *   empty `FilterObj` there, so the classic workspace filter bar is not shown
 *   for that tab. Environment-specific DST child screens (e.g. numeric
 *   `/workspace/11632`) can still define `filter_key`; add their `screen_key`
 *   to {@link SCREEN_KEYS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE} when they
 *   must keep selections across tax-year changes.
 * - Top-level Angular routes for DST (`app-routing.module.ts`: `statement-tracker`,
 *   `custom-report`, `section-*-report`, diagnostics paths, etc.) render **outside**
 *   `WorkspaceComponent` — no `gtw-data-filters`, so they are **not** affected by
 *   this reset path. Those components subscribe to `globalParamsChanged$` mainly
 *   to run `applyWorkspaceContext` once (`contextApplied` / `hasAppliedGtwParam`
 *   guards in reports, statements-filed-status, etc.) — they do not mirror the
 *   workspace filter-bar reset behaviour.
 *
 * Screens 11632 / 11633 (DST Admin Data push / PDF push to Efile, shared
 * filter_group 2446): keep non-year filter selections stable when the user
 * switches tax year; refresh dependent option lists via the normal
 * `cascadeByRefreshList` chain from `tax_year` instead of a full filter reset.
 */
const SCREEN_KEYS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE: ReadonlySet<number> = new Set([
  11632,
  11633,
  // EST module sibling tabs (EST Summary - Forecast/Annualized,
  // Download EST Lines Template, Download EST Extension Report).
  642,
  5046,
  10073,
  10875,
]);

/** Workspace menu `link` values that use the same preserve semantics as the set above. */
const SCREEN_LINKS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE: ReadonlySet<string> = new Set([
  'workspace.dst',
]);

const PDF_DD_AUTO_FETCH_SCREEN_KEY = 10710;
const PDF_DD_AUTO_FETCH_GRID_ID = '5441';
const PDF_DD_AUTO_FETCH_ACTION_ID = '31379';

@Component({
    selector: 'gtw-data-filters',
    templateUrl: './data-filters.component.html',
    styleUrls: ['./data-filters.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DataFiltersComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  filterObj: FilterObj | null = null;
  filters: any[] = [];
  /** OR-branch filters selected by `filter_by` (legacy `toggleSelectedFilter`). */
  toggleSelectedFilters: any[] = [];
  loading = false;
  showFilters = false;
  downloading = false;
  cascading = false;
  screenKey = 0;
  /**
   * True when the current workspace screen body is a custom element
   * (resolved via `SCREEN_CE_REGISTRY` in `workspace-screen.component.ts`)
   * rather than the standard `<gtw-grid>`. CE screens render their own
   * UI and have no grid to refresh, so the toolbar — including the
   * Refresh-only fallback for filter-less grids — must stay hidden for
   * them. Updated by `WorkspaceService.isCeScreen` whenever
   * `WorkspaceScreenComponent` resolves a new screen.
   */
  isCeScreen = false;
  private tabSub!: Subscription;
  private downloadDoneSub!: Subscription;
  private globalParamsSub!: Subscription;
  private isCeScreenSub!: Subscription;
  private refreshing = false;
  private lastScreenKey: string | null = null;
  /** Last tax/scenario/jcd seen on globalParamsChanged$ — used to avoid wiping screen filters on ME-only sync. */
  private prevGlobalFilterContext: { ty: string; sc: string; jcd: string } | null = null;
  /**
   * When the filter bar's `tax_year` control calls `syncTaxYearToGlobalAndReload`,
   * `globalParamsChanged$` runs synchronously inside `next()`. For preserve screens
   * we still cascade from the already-updated `tax_year` filter in that method —
   * skipping the subscriber's `alignTaxYearFilterFromGlobalAndCascade` avoids
   * running `cascadeByRefreshList` twice back-to-back.
   */
  private suppressSubscriberTaxYearAlign = false;
  /**
   * Screen-level flag (`activeScreen._filterContextDirty`): set when tax_year /
   * scenario / jcd changes without an explicit Filter click. While dirty, do not
   * auto-reload the grid via the `hadGridCache` → `setTabActive` path (avoids
   * premature "No data" snackbars). Cleared only in `refresh()`.
   */
  private static readonly FILTER_CONTEXT_DIRTY_KEY = '_filterContextDirty';
  isCurrentImport = false;
  /** Prevents overlapping `/getFilters` calls for import tabs (init + upload). */
  private importFilterLoadInFlight = false;
  private showFilterGroupListenerFn: any = null;

  private static readonly SCREEN_CALLBACK_MAP: Record<number, string> = {
    11442: 'addNewService',
    10898: 'stateCreateGroups',
    10895: 'createExternalEntity',
    11149: 'createNewOverrideEntity',
    11163: 'createScenarioEntity',
    11165: 'createReferenceTableEntity',
    11166: 'createConfigurationEntity',
    11167: 'createFilingDateEntity',
    11446: 'createRefreshSetupEntity',
    11451: 'createQuarterMapEntity',
    // efile-admin "Add New" pencil-click modals — each screen opens its own
    // legacy template (see ui/app/components/dataFilters/DataFiltersController.js
    // L255-L298). The generic `addNewEntity` callName has no registry entry,
    // so we route each screen directly to the migrated CE that already exists
    // in ComponentRegistryService (efile section).
    11462: 'efileSoftwareAddEditModal',          // efile-software-data-create-edit
    11463: 'efileAddEditReadOnly',               // efile-add-edit-read-only
    11464: 'efileAddtionalElements',             // efile-additional-elements
    11465: 'efileProperties',                    // efile-properties
    11466: 'efileSchemaSet',                     // efile-schema-set
    11467: 'efileDataDefinition',                // efile-data-definition
    11476: 'efileConfigPropertiesModal',         // efile-tmef-config
    11477: 'efileEntityMapModal',                // efile-tmef-entity-map
    11673: 'efileDiagnosticsConfigPropertiesModal', // efile-add-edit-diagnostics
    11468: 'createThresholdManagement',
    11555: 'create851ConfigEntity',
    11554: 'createLeOverride',
    11616: 'createSourcingOverride',
    11618: 'extractElementsFromXml',
  };

  constructor(
    private workspaceService: WorkspaceService,
    private globalService: GlobalService,
    private appService: AppService,
    private dataFiltersService: DataFiltersService,
    private componentRegistry: ComponentRegistryService,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef,
    private dialog: MatDialog,
    private gtwSnackbarService: GtwSnackbarService,
    private gridEventService: GridEventService,
    private tabGridLoader: TabGridLoaderService
  ) {}

  // Close open multiselect panels on a pointer-down outside the panel.
  // `mousedown` (not `click`): native <select> opens on mousedown and delays
  // the document `click`, which made the panel linger ~0.5s. Capture phase:
  // the AppKit nav stops click propagation in the bubble phase.
  private readonly onDocPointerDownCapture = (event: MouseEvent): void => {
    if (!this.filters?.some((f: any) => f._menuOpen)) return;
    const wrapper = (event.target as HTMLElement)?.closest?.('.gt-multiselect-wrapper');
    if (wrapper) return;
    this.filters.forEach((f: any) => (f._menuOpen = false));
    this.cdr.detectChanges();
  };

  ngOnInit(): void {
    // Track CE-vs-grid screen type. The Refresh-only toolbar fallback
    // (template gating in data-filters.component.html) must stay hidden
    // for CE screens that have no `<gtw-grid>` body — e.g.
    // `calcs-filing-group-data-setup`, `calcs-scenario-data-setup`, and
    // every other entry in `SCREEN_CE_REGISTRY`. `WorkspaceScreenComponent`
    // emits this BehaviorSubject whenever it resolves a screen's tag.
    this.isCeScreen = !!this.workspaceService.isCeScreen.value;
    this.isCeScreenSub = this.workspaceService.isCeScreen.subscribe((isCe: boolean) => {
      this.isCeScreen = !!isCe;
      this.cdr.detectChanges();
    });

    this.initFilters();

    this.tabSub = this.workspaceService.setTabActive.subscribe((ws: any) => {
      if (this.refreshing) {
        this.refreshing = false;
        return;
      }

      this.initFilters();
    });

    this.downloadDoneSub = this.workspaceService.downloadExcelDone.subscribe(() => {
      this.downloading = false;
      this.cdr.detectChanges();
    });

    this.globalParamsSub = this.globalService.globalParamsChanged$.subscribe(() => {
      const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
      const gp = this.globalService.global?.globalParams || {};
      const ty = String(gp.tax_year ?? '');
      const sc = String(gp.scenario ?? gp.scenario_v1 ?? '');
      const jcd = String(gp.jcd_key ?? '');
      const prev = this.prevGlobalFilterContext;
      const taxScenarioChanged =
        prev != null && (prev.ty !== ty || prev.sc !== sc || prev.jcd !== jcd);
      this.prevGlobalFilterContext = { ty, sc, jcd };

      const preserveFilters = this.shouldPreserveFiltersOnTaxScenarioChange(activeScreen);
      if (taxScenarioChanged && !preserveFilters) {
        // Tax-year / scenario / jcd_key change invalidates filter values
        // for the active screen AND all its siblings. Reason:
        // `WorkspaceService.getInheritedFilters` (called from
        // `initFilters` when a screen has `filter_inherit=1` and no
        // loaded FilterObj) walks the parent activity's `screens` array
        // and returns the FIRST sibling whose `filters?.loaded === true`
        // and whose `filter_key` matches. After visiting a sibling tab
        // (e.g. HO Branch Summary → HO Branch GAAP Detail and back), the
        // sibling holds a *reference* to the same FilterObj. Nulling
        // only `activeScreen.filters` therefore doesn't help: as soon as
        // `initFilters` re-runs, `getInheritedFilters` finds the sibling
        // still holding the now-stale FilterObj (loaded with the old
        // tax_year) and re-attaches it to the active screen — UI never
        // shows the cascade for the new year.
        //
        // Wipe sibling FilterObj references too so the next
        // `getInheritedFilters` returns null and `loadFilters` runs
        // against `/getFilters` with the fresh tax_year/scenario.
        //
        // Screens listed in
        // {@link SCREEN_KEYS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE} /
        // {@link SCREEN_LINKS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE}
        // opt out of this reset entirely — they keep their FilterObj
        // and refresh dependent option lists via
        // `alignTaxYearFilterFromGlobalAndCascade` below.
        const siblings = activeScreen?.parent?.screens
          || this.workspaceService.workspaceFactory.screens
          || [];
        for (const s of siblings) {
          if (s) s.filters = null;
        }
        if (activeScreen) activeScreen.filters = null;
        this.markFilterContextDirty(activeScreen);
      }
      this.initFilters();
      if (taxScenarioChanged && preserveFilters && !this.suppressSubscriberTaxYearAlign) {
        this.alignTaxYearFilterFromGlobalAndCascade();
      }
    });

    this.showFilterGroupListenerFn = this.gridEventService.on(
      'showFilterGroup',
      'dataFilters',
      (args: any) => {
        if (!this.isCurrentImport) return;
        if (args?.showFilter) {
          // Legacy `DataFiltersController.js` `$scope.$on('showFilterGroup', …)` — insurance / Sch P upload path.
          if (args.einNo) {
            this.globalService.setBslaFilterState(args.showFilter);
            const ein = String(args.einNo).replace(/[^\w\s]/gi, '');
            const ty = args.tax_year != null ? String(args.tax_year) : '';
            const chart = args.chart_type != null ? String(args.chart_type) : '';
            this.globalService.setEIN_TAXYEAR(ein, ty, chart);
          }
          const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
          this.ensureImportFiltersFromServer(activeScreen);
          if (activeScreen?.filters?.loaded) {
            this.filterObj = activeScreen.filters;
            this.filters = this.filterObj!.getFilters();
            this.showFilters = this.filters.length > 0;
            this.broadcastImportSaveGate();
          }
        } else {
          this.showFilters = false;
          this.filters = [];
        }
        this.cdr.detectChanges();
      }
    );

    document.addEventListener('mousedown', this.onDocPointerDownCapture, true);
  }

  hasScreenActionButtons(): boolean {
    this.screenKey = this.resolveScreenKey();
    return Object.prototype.hasOwnProperty.call(
      DataFiltersComponent.SCREEN_CALLBACK_MAP,
      this.screenKey
    );
  }

  private shouldPreserveFiltersOnTaxScenarioChange(activeScreen: any): boolean {
    const key = this.resolveScreenKeyForScreen(activeScreen);
    if (SCREEN_KEYS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE.has(key)) {
      return true;
    }
    const link = String(activeScreen?.link || activeScreen?.SCREEN_LINK || '').trim();
    return !!(link && SCREEN_LINKS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE.has(link));
  }

  /**
   * Same resolution as {@link resolveScreenKey} but accepts an explicit screen
   * reference so `globalParamsChanged$` can evaluate before `initFilters`
   * mutates view state.
   */
  private resolveScreenKeyForScreen(activeScreen: any): number {
    const candidates: any[] = [
      activeScreen?.screen_key,
      activeScreen?.data?.settings?.screen_key,
      this.globalService.global?.globalParams?.screen_key,
      (this.globalService as any)?.globalParams?.screen_key,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return 0;
  }

  private resolveScreenKey(): number {
    return this.resolveScreenKeyForScreen(this.workspaceService.workspaceFactory.activeScreen);
  }

  /**
   * Legacy `DataFiltersController.js` (grid-import): filter bar is hidden until the user
   * has template/grid data (`cachedData`), Sch P / insurance BSLA state, or has advanced past
   * step 1 (`importScreen.step >= 2` after upload / paste / batch load).
   */
  private isImportFilterBarContextVisible(activeScreen: any): boolean {
    if (!activeScreen) {
      return false;
    }
    if (IMPORT_SCREEN_TYPES.includes(activeScreen.screen_type)) {
      const step = activeScreen.importScreen?.step;
      return typeof step === 'number' && step >= 2;
    }
    const step = activeScreen.importScreen?.step;
    if (typeof step === 'number') {
      return step >= 2;
    }

    const rows = activeScreen?.data?.data;
    if (!Array.isArray(rows) || rows.length === 0) {
      return false;
    }
    if (rows.length > 1) {
      return true;
    }

    const row = rows[0] || {};
    const meaningful = Object.keys(row).filter((k) => {
      const v = row[k];
      if (v == null || v === '') {
        return false;
      }
      if (k === 'object_id' && String(v) === '1') {
        return false;
      }
      return true;
    });
    return meaningful.length > 0;
  }

  private isImportLikeScreen(activeScreen: any): boolean {
    if (!activeScreen) {
      return false;
    }
    if (IMPORT_SCREEN_TYPES.includes(activeScreen.screen_type)) {
      return true;
    }
    return String(activeScreen?.data?.settings?.grid_type || '').toLowerCase() === 'import';
  }

  /**
   * Ensures grid-import tabs call `/getFilters` when the screen has a `filter_key` but
   * `activeScreen.filters` is missing, is the filter-less placeholder from {@link initFilters}
   * when `filter_key` was temporarily absent, or is loaded with zero definitions.
   * Invoked from the import branch of {@link initFilters} and from `showFilterGroup` after upload.
   */
  private ensureImportFiltersFromServer(activeScreen: any): void {
    if (!activeScreen?.filter_key) {
      return;
    }
    if (!this.isImportLikeScreen(activeScreen)) {
      return;
    }

    const fo = activeScreen.filters as any;
    const isPlaceholder = fo?.__syntheticEmptyFilterBar === true;
    const hasRealDefinitions = !!(fo?.loaded && (fo.filters?.length ?? 0) > 0);
    if (hasRealDefinitions && !isPlaceholder) {
      return;
    }
    if (this.importFilterLoadInFlight) {
      return;
    }

    this.importFilterLoadInFlight = true;
    activeScreen.filters = null;
    this.filterObj = null;
    this.filters = [];
    this.showFilters = false;

    const fk = String(activeScreen.filter_key);
    this.dataFiltersService
      .loadFilters(fk)
      .then((loadedFo) => {
        const merged = this.mergeFilterLoadResult(activeScreen, loadedFo);
        this.filterObj = merged;
        activeScreen.filters = merged;
        const reveal = this.isImportFilterBarContextVisible(activeScreen);
        this.filters = reveal ? merged.getFilters() : [];
        this.showFilters = reveal && this.filters.length > 0;
        if (reveal) this.broadcastImportSaveGate();
        this.cdr.detectChanges();
      })
      .finally(() => {
        this.importFilterLoadInFlight = false;
      });
  }

  private initFilters(): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    this.screenKey = this.resolveScreenKey();
    const isImport = this.isImportLikeScreen(activeScreen);
    this.isCurrentImport = isImport;

    if (!activeScreen?.filter_key) {
      this.showFilters = false;
      this.filters = [];

      if (!activeScreen.filters) {
        const synth = new FilterObj();
        synth.setLoaded(true);
        (synth as any).__syntheticEmptyFilterBar = true;
        activeScreen.filters = synth;
      }
      // Assign the (possibly pre-existing) synth FilterObj to the
      // component so the toolbar's Refresh button has a valid target —
      // the template now renders the bar unconditionally, so we must
      // ensure refresh() can call setFilterParams() / checkRequiredFilters()
      // without bailing on a null filterObj.
      this.filterObj = activeScreen.filters;

      this.workspaceService.dataFetched.next(true);
      this.cdr.detectChanges();
      return;
    }

    if (isImport) {
      this.workspaceService.dataFetched.next(true);

      if (activeScreen.filters?.loaded && activeScreen.filters?.filters?.length > 0) {
        this.filterObj = activeScreen.filters;
        const reveal = this.isImportFilterBarContextVisible(activeScreen);
        this.filters = reveal ? this.filterObj!.getFilters() : [];
        this.showFilters = reveal && this.filters.length > 0;
        if (reveal) this.broadcastImportSaveGate();
        this.cdr.detectChanges();
        return;
      }

      this.showFilters = false;
      this.filters = [];

      this.ensureImportFiltersFromServer(activeScreen);
      if (activeScreen.filters?.loaded) {
        this.filterObj = activeScreen.filters;
        const reveal = this.isImportFilterBarContextVisible(activeScreen);
        this.filters = reveal ? this.filterObj!.getFilters() : [];
        this.showFilters = reveal && this.filters.length > 0;
        if (reveal) this.broadcastImportSaveGate();
      }
      return;
    }

    const forceFilterReload = activeScreen?.commentLink?.forceFilterReload === true;
    if (forceFilterReload && activeScreen.filters?.loaded) {
      activeScreen.filters = null;
    }

    if (activeScreen.filters?.loaded) {
      this.filterObj = activeScreen.filters;
      this.filters = this.filterObj!.getFilters();
      this.refreshToggleSelectedFilters();
      this.showFilters = true;
      this.workspaceService.cacheFilterObj(activeScreen);
      void this.maybeDefaultFilterByThenCascade().then(() => this.cdr.detectChanges());
      const isDrill = this.isDrillDownLink(activeScreen);
      if (isDrill) {
        this.applyCommentLinkFilters(activeScreen, true);
      } else {
        // Tab return: keep grid visible if this screen already ran Filter once
        // (GridDirective caches the payload on activeScreen.data). After a tax
        // year / scenario change without Filter, stay on the placeholder.
        const showGrid =
          !this.isFilterContextDirty(activeScreen) &&
          this.screenHasSuccessfulGridCache(activeScreen);
        this.workspaceService.dataFetched.next(showGrid);
        this.cdr.detectChanges();
      }
      return;
    }

    this.showFilters = false;
    this.filters = [];
    this.loading = true;
    this.cdr.detectChanges();

    this.workspaceService.dataFetched.next(false);

    if (activeScreen.filter_key && !forceFilterReload) {
      const inheritedFilters = this.workspaceService.getInheritedFilters(activeScreen);
      if (inheritedFilters) {
        activeScreen.filters = inheritedFilters;
        this.filterObj = inheritedFilters;
        this.filters = this.filterObj!.getFilters();
        this.refreshToggleSelectedFilters();
        this.showFilters = true;
        this.loading = false;
        this.workspaceService.cacheFilterObj(activeScreen);
        void this.maybeDefaultFilterByThenCascade().then(() => this.cdr.detectChanges());
        const isDrill = this.isDrillDownLink(activeScreen);
        if (isDrill) {
          this.applyCommentLinkFilters(activeScreen, true);
        } else {
          const showGrid =
            !this.isFilterContextDirty(activeScreen) &&
            this.screenHasSuccessfulGridCache(activeScreen);
          this.workspaceService.dataFetched.next(showGrid);
          this.cdr.detectChanges();
        }
        return;
      }
    }

    this.dataFiltersService.loadFilters(activeScreen.filter_key).then((fo) => {
      const merged = this.mergeFilterLoadResult(activeScreen, fo);
      this.filterObj = merged;
      activeScreen.filters = merged;
      const allFilters = merged.getFilters();
      this.filters = allFilters;
      this.refreshToggleSelectedFilters();
      this.showFilters = merged.filters.length > 0;
      this.loading = false;
      this.workspaceService.cacheFilterObj(activeScreen);
      const hadGridCache = this.screenHasSuccessfulGridCache(activeScreen);
      if (this.isDrillDownLink(activeScreen)) {
        // Drill-down pending: apply commentLink filters (ME, scenario,
        // etc. from the source tab) and trigger grid load. Without this,
        // data only appears after the user leaves the tab and comes back.
        this.applyCommentLinkFilters(activeScreen, true);
      } else if (hadGridCache) {
        // e.g. getFilters failed after a refetch but merge kept prior FilterObj + grid payload
        if (this.isFilterContextDirty(activeScreen)) {
          // Tax year / scenario changed in the UI but the user has not clicked
          // Filter — keep the "use filters above" placeholder; do not replay
          // cache or POST loadGridJson (which would show a stale or empty grid
          // and fire the "No data" snackbar).
          this.workspaceService.dataFetched.next(false);
          this.cdr.detectChanges();
        } else {
          this.workspaceService.dataFetched.next(true);
          // Re-emit setTabActive so GridDirective can re-initialize gizmoGrid
          // with the freshly-loaded FilterObj. This path runs when filters were
          // nulled mid-flight (globalParamsChanged$ -> taxScenarioChanged -> set
          // activeScreen.filters = null) and loadFilters has just resolved. The
          // GridDirective's tabSub guards a same-screen re-emit by KEEPING the
          // current grid alive while filters.loaded === false; this re-emit lets
          // it finally tear down and re-init now that filters are ready (e.g.
          // tab return after filters were wiped for another reason).
          // refreshing=true keeps our own tabSub from looping back into
          // initFilters() for this synthetic emission.
          this.refreshing = true;
          this.workspaceService.setTabActive.next(activeScreen);
        }
      } else if (this.shouldAutoFetchAfterFilterLoad(activeScreen, allFilters)) {
        this.workspaceService.dataFetched.next(true);
        this.cdr.detectChanges();
        this.refreshing = true;
        this.workspaceService.setTabActive.next(activeScreen);
        return;
      }

      // Backend returned zero filters (or callSuccess !== '1' so loadFilters
      // resolved with an empty FilterObj). Treat this exactly like the
      // "no filter_key" branch above: there is no Filter button to click,
      // so emit dataFetched so GridDirective can stop bailing on
      // "filters not loaded" and load the grid right away.
      //
      // Critically we ALSO have to re-emit `setTabActive` here. Background:
      // the original `setTabActive` from `WorkspaceService.loadTab()` /
      // `setActivity()` reaches `GridDirective.tabSub` BEFORE
      // `loadFilters(filter_key)` has resolved. `initGrid()` therefore bails
      // with "filters not loaded" and never tries again — its only re-init
      // path is another `setTabActive.next(activeScreen)` emission. For
      // screens with non-empty filters the user clicks the Filter button
      // (which `refresh()` translates into a `setTabActive` re-emit); for
      // the drill-down branch we already re-emit via
      // `applyCommentLinkFilters`; for the `hadGridCache` branch a re-emit
      // is fired below. The empty-filters branch was missing the same
      // signal — which is why screens whose `/getFilters` legitimately
      // returns zero filters stayed stuck on a permanent
      // "[GridDirective] initGrid bail: filters not loaded" with no grid.
      // `refreshing = true` keeps our own `tabSub` from looping back into
      // `initFilters()` for this synthetic emission.
      if (!allFilters || allFilters.length === 0) {
        this.workspaceService.dataFetched.next(true);
        this.cdr.detectChanges();
        this.refreshing = true;
        this.workspaceService.setTabActive.next(activeScreen);
        return;
      }

      // For non-drilldown fresh visits with real filters: do NOT emit
      // dataFetched — the "Please use filters above" placeholder stays
      // until the user clicks the Filter button.
    });
  }

  private isDrillDownLink(activeScreen: any): boolean {
    const t = activeScreen?.commentLink?.type;
    return t === 'drillDown' || t === 'link';
  }

  private shouldAutoFetchAfterFilterLoad(activeScreen: any, filters: any[]): boolean {
    if (!filters?.length || Number(activeScreen?.screen_key) !== PDF_DD_AUTO_FETCH_SCREEN_KEY) {
      return false;
    }

    const apiGet = this.appService.ROUTES?.[activeScreen?.link]?.api?.get || '';
    return (
      this.hasQueryParamValue(apiGet, 'grid_id', PDF_DD_AUTO_FETCH_GRID_ID) &&
      this.hasQueryParamValue(apiGet, 'action_id', PDF_DD_AUTO_FETCH_ACTION_ID)
    );
  }

  private hasQueryParamValue(url: string, paramName: string, expectedValue: string): boolean {
    const text = String(url || '');
    const queryStart = text.indexOf('?');
    if (queryStart === -1) {
      return false;
    }

    return text
      .slice(queryStart + 1)
      .split('&')
      .some((part: string) => {
        const [key, value = ''] = part.split('=');
        return decodeURIComponent(key) === paramName && decodeURIComponent(value) === expectedValue;
      });
  }

  /**
   * If `filter_by` never received a selection, OR-branch filters stay empty in
   * `toggleSelectedFilter`. Default first `filter_by` option and cascade.
   */
  private async maybeDefaultFilterByThenCascade(): Promise<void> {
    if (!this.filterObj) return;
    const filters = this.filterObj.getFilters();
    const fb = filters.find((f: any) => f.param_name === 'filter_by');
    if (!fb || fb.selected || !fb.values?.length) {
      this.refreshToggleSelectedFilters();
      return;
    }
    if (fb.multiple === 1) {
      this.refreshToggleSelectedFilters();
      return;
    }
    const ui = String(fb.ui_type || '').trim();
    const multiUi = ['df-me-drop-down', 'df-select-multiple', 'df-select-virtual-scroll', 'df-select-single'];
    if (multiUi.includes(ui)) {
      this.refreshToggleSelectedFilters();
      return;
    }
    fb.selected = fb.values[0];
    await this.dataFiltersService.cascadeByRefreshList(fb, this.filterObj, undefined);
    this.refreshToggleSelectedFilters();
  }

  /** Legacy `grid-filters-tpl.html`: main loop excludes `is_or_conditional_filter` filters. */
  isMainBarFilter(filter: any): boolean {
    return !filter?.is_or_conditional_filter;
  }

  /**
   * Legacy parity: OR-branch filters (`is_or_conditional_filter`) render only
   * when their `filter_key` matches the current `filter_by` selection
   * (`toggleSelectedFilter` / `toggleSelectedFilters`).
   */
  isHiddenByFilterBy(filter: any): boolean {
    if (!filter?.is_or_conditional_filter) {
      return false;
    }
    return !this.toggleSelectedFilters.some(
      (f: any) => String(f?.filter_key) === String(filter?.filter_key)
    );
  }

  /**
   * Sync OR-branch toolbar filters from `filter_by` selection (legacy
   * `toggleSelectedFilter` / `setToggleFilterGroup`).
   */
  private refreshToggleSelectedFilters(): void {
    if (!this.filterObj) {
      this.toggleSelectedFilters = [];
      return;
    }
    const toggleFilters = this.filters.filter((f: any) => !!f.is_or_conditional_filter);
    this.filterObj.setToggleFilterGroup(toggleFilters);
    this.toggleSelectedFilters = [...(this.filterObj.toggleSelectedFilter || [])];
  }

  /**
   * Prefer a successful filter payload; if getFilters failed (401, network) but this tab
   * already had definitions + selections, keep the existing FilterObj so the bar and grid cache survive.
   */
  private mergeFilterLoadResult(activeScreen: any, fo: FilterObj): FilterObj {
    const existing = activeScreen?.filters as FilterObj | null | undefined;
    const incomingOk = fo?.loaded === true && (fo.filters?.length ?? 0) > 0;
    if (incomingOk) return fo;
    if (
      existing?.loaded === true
      && (existing.filters?.length ?? 0) > 0
      && (!fo?.loaded || !(fo.filters?.length))
    ) {
      return existing;
    }
    return fo;
  }

  private markFilterContextDirty(activeScreen: any): void {
    if (activeScreen) {
      activeScreen[DataFiltersComponent.FILTER_CONTEXT_DIRTY_KEY] = true;
    }
  }

  private isFilterContextDirty(activeScreen: any): boolean {
    return !!activeScreen?.[DataFiltersComponent.FILTER_CONTEXT_DIRTY_KEY];
  }

  private clearFilterContextDirty(activeScreen: any): void {
    if (activeScreen) {
      activeScreen[DataFiltersComponent.FILTER_CONTEXT_DIRTY_KEY] = false;
    }
  }

  /** True when GridDirective has stored a successful grid payload for this tab (post-Filter). */
  private screenHasSuccessfulGridCache(activeScreen: any): boolean {
    let d = activeScreen?.data;
    // Fallback: if the screen object was re-created (fresh USER_MENU ref on
    // setActivity) and hasn't been re-hydrated yet, read from the singleton
    // TabGridLoader cache which survives screen ref churn. Re-hydrate onto
    // the screen so downstream consumers (GridDirective.loadGridData cache
    // fast path) see it immediately.
    if (!d && activeScreen?.screen_key) {
      const cached = this.tabGridLoader?.getCached?.(activeScreen.screen_key);
      if (cached) {
        activeScreen.data = cached;
        activeScreen.cachedData = true;
        d = cached;
      }
    }
    if (!d) return false;
    const cs = d.callSuccess;
    const ok = cs === '1' || cs === 1 || String(cs) === '1';
    return ok && (d.header != null || Array.isArray(d.data));
  }

  private applyCommentLinkFilters(activeScreen: any, triggerRefresh = false): void {
    const commentFilters = activeScreen?.commentLink?.filters;

    if (!this.filterObj || !Array.isArray(commentFilters) || !commentFilters.length) {
      this.cdr.detectChanges();
      this.workspaceService.dataFetched.next(true);
      if (triggerRefresh) {
        this.refreshing = true;
        this.workspaceService.setTabActive.next(activeScreen);
      }
      return;
    }

    console.log('[DataFilters] applyCommentLinkFilters: has commentFilters, triggerRefresh:', triggerRefresh);
    this.workspaceService.dataFetched.next(true);

    this.cascading = true;
    this.cdr.detectChanges();

    this.dataFiltersService.applyCommentLinkFilters(this.filterObj, commentFilters).then((changed: boolean) => {
      console.log('[DataFilters] cascade done, changed:', changed, 'triggerRefresh:', triggerRefresh);
      this.cascading = false;
      this.cdr.detectChanges();
      if (triggerRefresh) {
        // Re-emit setTabActive after applying drilldown filters so
        // GridDirective gets a second initialization pass with
        // dataFetched=true. This must happen even when `changed` is false:
        // the first setTabActive from WorkspaceService.loadLink can reach
        // GridDirective before this async filter application completes, so
        // initGrid bails with dataFetched=false and no grid POST is fired.
        // Using refreshGridData would fail when the grid was never
        // initialized (ggApi is null on first-visit tabs).
        this.refreshing = true;
        this.workspaceService.setTabActive.next(activeScreen);
      }
    });
  }

  onFilterChange(filter: any, event: any): void {
    const val = event?.target?.value;
    if (filter.multiple === 1) return;

    if (filter.values?.length) {
      const vn = filter.value_name || 'VALUE';
      const match = _.find(filter.values, (v: any) => {
        const key = v?.[vn] ?? v?.VALUE ?? v?.value;
        return String(key) === String(val);
      });
      filter.selected = match || filter.values[0];
    }

    if (filter.param_name === 'filter_by') {
      this.resetHiddenToggleFilters(filter);
      this.refreshToggleSelectedFilters();
    }

    // Tax year is the root of the filter chain — every other selection
    // (scenario, filing group, MEs, etc.) is scoped to the previous year and
    // becomes stale. Reuse the global-header path: sync GlobalService to the
    // new year and fire globalParamsChanged$. The same listener that reacts
    // to the header dropdown (ngOnInit, line ~117) nulls activeScreen.filters
    // and re-runs initFilters(), which calls /getFilters fresh — identical
    // to the very first open of the tab. No partial cascade, no hand-rolled
    // per-control reset; just the single canonical reload pipeline.
    if (filter.param_name === 'tax_year') {
      this.syncTaxYearToGlobalAndReload(filter);
      return;
    }

    this.cdr.detectChanges();
    this.triggerCascade(filter);
    this.maybeUpdateImportSaveButtonState(filter);
  }

  private resetHiddenToggleFilters(filterBy: any): void {
    const vn = filterBy.value_name || 'VALUE';
    const selectedValue = String(filterBy.selected?.[vn] ?? filterBy.selected?.VALUE ?? '');

    this.filters.forEach((f: any) => {
      if (f.is_or_conditional_filter && String(f.filter_key) !== selectedValue) {
        f.selected = _.isArray(f.selected) ? [] : null;
      }
    });
  }

  /**
   * For screens in {@link SCREEN_KEYS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE},
   * the global header (or any other path) may advance `GlobalService` while the
   * in-memory `tax_year` filter row still reflects the prior selection. Align the
   * control, then run the same cascade used by manual filter edits so dependent
   * lists refresh for the new year without discarding unrelated selections.
   */
  private alignTaxYearFilterFromGlobalAndCascade(): void {
    const global = this.globalService.global;
    const gas = global?.inputs?.tax_year?.selected;
    if (!this.filterObj || !gas) {
      return;
    }

    const filters = this.filterObj.getFilters();
    const tyf = filters.find((f: any) => f.param_name === 'tax_year');
    if (!tyf) {
      return;
    }

    const vn = tyf.value_name || 'VALUE';
    const want = String(gas.value ?? gas.VALUE ?? '');
    if (tyf.values?.length && want) {
      const match = _.find(tyf.values, (v: any) => {
        const key = String(v?.[vn] ?? v?.VALUE ?? v?.value ?? '');
        return key === want;
      });
      if (match) {
        tyf.selected = match;
      }
    }

    this.triggerCascade(tyf);
  }

  /**
   * Local tax-year dropdown change → mirror the global-header tax-year flow
   * (`AppComponent.onSetTaxYearScenario` + `onGlobalParamsChanged`) so the
   * filter bar is rebuilt by the same `globalParamsChanged$` subscriber that
   * already handles header changes. That subscriber nulls
   * `activeScreen.filters` and calls `initFilters()`, which in turn invokes
   * `dataFiltersService.loadFilters(activeScreen.filter_key)` — the same
   * `/getFilters` request that runs on the screen's first open. The
   * `activeScreen.filters = null` line below is deliberate: the listener only
   * nulls when its own `prevGlobalFilterContext` is non-null (i.e. it has
   * already observed at least one global change), so on the first local
   * tax-year change of the session we still need to clear the cached object
   * here to force the fresh API call — except for screens listed in
   * {@link SCREEN_KEYS_PRESERVE_FILTERS_ON_TAX_SCENARIO_CHANGE}, where the
   * filter object is kept and dependents are refreshed via cascade only.
   */
  private syncTaxYearToGlobalAndReload(taxYearFilter: any): void {
    const newSelected = taxYearFilter?.selected;
    if (!newSelected) return;

    const global = this.globalService.global;
    if (!global?.inputs?.tax_year || !global?.globalParams) return;

    global.inputs.tax_year.selected = newSelected;
    global.globalParams.tax_year = newSelected.value;
    global.globalParams.scenario = newSelected.scenario;
    global.globalParams.scenario_v1 = newSelected.scenario;
    global.globalParams.scenarioCode = newSelected.scenario_type_code;
    global.globalParams.scenario_desc = newSelected.scenario_desc;
    global.globalParams.is_issue_key_enabled = newSelected.is_issue_key_enabled;

    const selectedScenario: any = _.find(newSelected.scenarios || [], {
      scenario: newSelected.scenario,
    });
    if (selectedScenario) {
      global.globalParams.jcd_key = selectedScenario.jcd_key;
      global.globalParams.is_locked = selectedScenario.is_locked;
      global.globalParams.scenarioCode = selectedScenario.scenario_type_code;
      global.globalParams.is_issue_key_enabled = selectedScenario.is_issue_key_enabled;
    }

    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const preserve = this.shouldPreserveFiltersOnTaxScenarioChange(activeScreen);
    if (activeScreen?.filters && !preserve) {
      activeScreen.filters = null;
    }
    if (!preserve) {
      this.markFilterContextDirty(activeScreen);
    }

    if (preserve) {
      this.suppressSubscriberTaxYearAlign = true;
    }
    this.globalService.globalParamsChanged$.next();
    if (preserve) {
      this.suppressSubscriberTaxYearAlign = false;
      this.triggerCascade(taxYearFilter);
    }
  }

  refresh(): void {
    // Filter-less grids (no `filter_key` on the screen — e.g. Calcs Log
    // and the other legacy `grid-standard-tpl.html` diagnostic screens)
    // still expose the Refresh icon in the toolbar. There is no FilterObj
    // to validate / serialize in that case, so skip the filter checks and
    // go straight to the cache-clear + setTabActive refresh path.
    if (this.filterObj) {
      // Legacy parity: `DataFiltersService.js:683-749` `FilterObj.checkRequiredFilters` —
      // mirrored in `data-filters.service.ts:139-203` (OR-group via `filters_or_group_setup`,
      // per-filter `required==1`, combined "A (OR) B" message). Refresh-button validation only.
      const { valid, messages } = this.filterObj.checkRequiredFilters();
      if (!valid) {
        const msg = messages.length
          ? messages.join('; ')
          : 'Required filters are missing. Please select a value.';
        this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--orange');
        return;
      }
      this.filterObj.setFilterParams();
    }

    this.filters.forEach((f: any) => f._menuOpen = false);

    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    this.clearFilterContextDirty(activeScreen);
    this.workspaceService.clearCache(activeScreen);

    // Flip the tab dot to "loading" IMMEDIATELY so the blinking animation
    // is already visible by the time the synchronous setTabActive chain
    // runs the first `cdr.detectChanges()` (in WorkspaceComponent's
    // `gridLoading` subscriber). Otherwise `loading_data` is only set
    // deep inside `TabGridLoaderService.fetchForScreen` — AFTER that CD
    // pass — so the dot stays gray (clearCache flipped cachedData=false)
    // until ZoneJS auto-ticks. For a fast cached HTTP response that tick
    // can land alongside `processAndLoadData` flipping loading_data back
    // to false, and the user never perceives the blink.
    activeScreen.loading_data = true;

    this.workspaceService.dataFetched.next(true);

    this.refreshing = true;
    this.workspaceService.setTabActive.next(activeScreen);
  }

  downloadExcel(): void {
    this.downloading = true;
    this.cdr.detectChanges();
    this.workspaceService.downloadExcel.next();
  }

  getDisplayValue(filter: any, option: any): string {
    const ln = filter.label_name;
    if (ln && option?.[ln] !== undefined) return option[ln];
    return option?.NAME ?? option?.name ?? option?.label ?? option?.LABEL ??
      option?.VALUE ?? option?.value ?? '';
  }

  getOptionValue(filter: any, option: any): string {
    const vn = filter.value_name || 'VALUE';
    return String(option?.[vn] ?? option?.VALUE ?? option?.value ?? '');
  }

  getSelectedValue(filter: any): string {
    if (!filter.selected) return '';
    const vn = filter.value_name || 'VALUE';
    return String(filter.selected[vn] ?? filter.selected.VALUE ?? filter.selected.value ?? '');
  }

  // Use array index as option value to avoid duplicate VALUE fields selecting wrong entry
  getSelectedIndex(filter: any): number {
    if (!filter.selected || !filter.values?.length) return 0;
    const idx = filter.values.indexOf(filter.selected);
    return idx >= 0 ? idx : 0;
  }

  // Select filter option by index to handle filters with duplicate VALUE fields (e.g., FTC)
  onFilterChangeByIndex(filter: any, event: any): void {
    const idx = parseInt(event?.target?.value, 10);
    if (!isNaN(idx) && filter.values?.[idx]) {
      filter.selected = filter.values[idx];
    } else {
      filter.selected = filter.values?.[0] || null;
    }

    if (filter.param_name === 'filter_by') {
      this.resetHiddenToggleFilters(filter);
      this.refreshToggleSelectedFilters();
    }

    if (filter.param_name === 'tax_year') {
      this.syncTaxYearToGlobalAndReload(filter);
      return;
    }

    this.cdr.detectChanges();
    this.triggerCascade(filter);
    this.maybeUpdateImportSaveButtonState(filter);
  }

  private readonly MULTI_TYPES = [
    'df-me-drop-down', 'df-select-multiple',
    'df-select-virtual-scroll', 'df-select-single'
  ];

  isMultiSelectType(filter: any): boolean {
    return filter.multiple === 1 || this.MULTI_TYPES.includes(filter.ui_type);
  }

  isMeDropdown(filter: any): boolean {
    return filter.ui_type === 'df-me-drop-down';
  }

  getMeSelectedKeys(filter: any): string {
    if (!filter.selected || !Array.isArray(filter.selected) || !filter.selected.length) return '';
    return filter.selected
      .map((s: any) => s?.key || s?.KEY || s?.mekey || s?.VALUE || s?.value || '')
      .filter(Boolean)
      .join(',');
  }

  onMeSelectionChanged(filter: any, selectedKeys: string): void {
    if (!selectedKeys) {
      filter.selected = [];
    } else {
      const vn = filter.value_name || 'VALUE';
      const keys = selectedKeys.split(',').map((k: string) => k.trim()).filter(Boolean);

      const matched: any[] = [];
      if (filter.values?.length) {
        const keySet = new Set(keys);
        const collect = (nodes: any[]) => {
          for (const node of nodes) {
            const nk = String(node?.key ?? node?.KEY ?? node?.VALUE ?? node?.ME_KEY ?? '');
            if (nk && keySet.has(nk)) {
              const { children, ...rest } = node;
              matched.push(rest);
            }
            if (node?.children?.length) {
              collect(node.children);
            }
          }
        };
        collect(filter.values);
      }

      if (matched.length) {
        filter.selected = matched;
      } else {
        filter.selected = keys.map((k: string) => {
          const obj: any = { key: k, parnet: k };
          obj[vn] = k;
          return obj;
        });
      }
    }
    this.cdr.detectChanges();
    this.triggerCascade(filter);
    // Legacy parity: `DataFiltersDirectives.js` only broadcasts
    // `showSaveButton` from `dfSelect.change` (single-select). The ME
    // directive (`dfMeDropDown`) never participates in the import Save
    // gating — see `maybeUpdateImportSaveButtonState` JSDoc.
  }

  private triggerCascade(filter: any): void {
    if (!this.filterObj) return;

    const hasDependents = this.dataFiltersService.hasDependentFilters(filter, this.filterObj);
    if (hasDependents) {
      this.cascading = true;
      this.cdr.detectChanges();
    }

    this.dataFiltersService.cascadeByRefreshList(filter, this.filterObj).then(() => {
      this.cascading = false;
      this.cdr.detectChanges();
    });
  }

  /**
   * Legacy parity for import Save-button gating.
   *
   * Source: `ui/app/components/dataFilters/DataFiltersDirectives.js`,
   * `dfSelect.change` (lines 553-583), which was the ONLY place that
   * broadcast `showSaveButton`.
   *
   * Important behavior to preserve exactly:
   *   - Group 25 (`fk 30/31/32`) fires only for those keys.
   *   - Screens 237 / 11471 evaluate on EVERY dfSelect change:
   *       fk 51 + non-null -> enable Save (`showBtn=false`)
   *       anything else    -> disable Save (`showBtn=true`)
   *   - Screens 11287 / 11294 / 11324 / 11327 also evaluate on EVERY
   *     dfSelect change:
   *       fk 1467/1468 + non-null -> enable Save
   *       anything else            -> disable Save
   *
   * The migrated implementation previously added a narrower filter-key gate
   * for the insurance branches, which left Save enabled when a non-target
   * single-select changed. Reverting to strict legacy branch semantics keeps
   * enable/disable behavior identical to AngularJS.
   */
  private maybeUpdateImportSaveButtonState(filter: any): void {
    const fk = Number(filter?.filter_key);
    const sk = Number(this.globalService.global?.globalParams?.screen_key);

    const sel = filter?.selected;
    const hasValue =
      sel != null &&
      sel !== '' &&
      !(Array.isArray(sel) && sel.length === 0);

    let showBtn: boolean | null = null;

    // BSLA / Sch P group 25 — fires only when the changed filter is one of
    // the three group-25 filter_keys (legacy `dfSelect.change` line 556).
    if (fk === 30 || fk === 31 || fk === 32) {
      showBtn = fk !== 32;
    } else if (sk === 237 || sk === 11471) {
      // Legacy: ANY dfSelect change on these screens toggles Save.
      showBtn = fk === 51 && hasValue ? false : true;
    } else if (
      sk === 11287 || sk === 11294 || sk === 11324 || sk === 11327
    ) {
      // Legacy: ANY dfSelect change on these screens toggles Save.
      showBtn = (fk === 1467 || fk === 1468) && hasValue ? false : true;
    }

    if (showBtn !== null) {
      this.globalService.setInsuranceSaveBtnState(showBtn);
      this.gridEventService.broadcast('showSaveButton', { showBtn });
    }
  }

  /**
   * Initial Save-button gating for group-25 import screens
   * (K2/K3 "Import Other Part & Sections", filter keys 30/31/32).
   *
   * `maybeUpdateImportSaveButtonState` only fires on a single-select *change*,
   * so when the import grid is first revealed (upload / paste / batch load /
   * tab return) the Save button keeps whatever state it had — which left it
   * enabled before any Form / Schedule selection. This evaluates the current
   * fk-32 selection and broadcasts the matching state so Save starts disabled
   * until the gating filter is chosen, mirroring the `fk === 32` enable rule.
   *
   * Insurance screens (237 / 11471 / 11287 / 11294 / 11324 / 11327) persist
   * their gate via `GlobalService` and are intentionally left untouched here.
   */
  private broadcastImportSaveGate(): void {
    if (!this.isCurrentImport) return;
    const filters = this.filters || [];
    const isGroup25 = filters.some(
      (f: any) => [30, 31, 32].includes(Number(f?.filter_key))
    );
    if (!isGroup25) return;

    const enabled = filters.some((f: any) => {
      if (Number(f?.filter_key) !== 32) return false;
      const sel = f?.selected;
      return sel != null && sel !== '' && !(Array.isArray(sel) && sel.length === 0);
    });
    const showBtn = !enabled;
    this.globalService.setInsuranceSaveBtnState(showBtn);
    this.gridEventService.broadcast('showSaveButton', { showBtn });
  }

  isFilterLoading(filter: any): boolean {
    return !!filter.loading;
  }

  isTextFilter(filter: any): boolean {
    return filter.ui_type === 'df-text' || filter.data_type === 'text';
  }

  getTextFilterValue(filter: any): string {
    if (!filter?.selected) {
      return '';
    }
    const vn = filter.value_name || 'VALUE';
    const raw = filter.selected[vn] ?? filter.selected.VALUE ?? filter.selected.value ?? '';
    return raw == null ? '' : String(raw);
  }

  onTextFilterChange(filter: any, event: any): void {
    let value = event?.target?.value ?? '';
    value = value.replace(/[^a-zA-Z0-9\-_\s]/g, '');
    const vn = filter.value_name || 'VALUE';
    filter.selected = { VALUE: value };
    if (vn !== 'VALUE') {
      filter.selected[vn] = value;
    }
    if (event?.target) {
      event.target.value = value;
    }
    this.cdr.detectChanges();
    this.triggerCascade(filter);
  }

  isSingleSelectType(filter: any): boolean {
    return !this.isMultiSelectType(filter)
      && filter.ui_type !== 'df-select-filter-by'
      && !this.isTextFilter(filter);
  }

  isSingleSelectDropdown(filter: any): boolean {
    return filter.ui_type === 'df-select-single';
  }

  toggleMultiMenu(filter: any): void {
    const wasOpen = filter._menuOpen;
    this.filters.forEach((f: any) => {
      if (f !== filter) f._menuOpen = false;
    });
    filter._menuOpen = !wasOpen;
  }

  getMultiCount(filter: any): number {
    if (!filter.selected) return 0;
    return _.isArray(filter.selected) ? filter.selected.length : 0;
  }

  getFilteredMultiValues(filter: any): any[] {
    if (!filter.values) return [];
    const search = (filter._searchText || '').toLowerCase();
    if (!search) return filter.values.slice(0, 50);
    return filter.values.filter((v: any) => {
      const lbl = (v.LABEL || v.NAME || v.name || v.label || v.VALUE || '').toLowerCase();
      return lbl.includes(search);
    }).slice(0, 50);
  }

  /**
   * Resolve a single normalized string key for a multi-select option.
   *
   * The toolbar's "Selected ..." dropdown must decide whether an option
   * is already in `filter.selected`. The previous implementation compared
   * `s[vn] === opt[vn] || s.VALUE === opt.VALUE`, which silently produced
   * `undefined === undefined` (→ true) whenever `value_name` (e.g. "KEY",
   * "LEID", "HO_LEID") was not a property on the row objects returned by
   * `/getFilters`. The `||` short-circuited before the real VALUE check
   * could fire, so every visible row rendered as checked after a single
   * pick — see the Ho Leid global filter bug.
   *
   * Mirroring `FilterObj.setFilterParams` / `buildCurrentFilterParams` in
   * `data-filters.service.ts`, walk the same `??` fallback chain
   * (`value_name`, then VALUE/value/key/KEY/mekey/parnet) and stringify
   * once so numeric-vs-string drift from the backend doesn't matter.
   */
  private getMultiOptionKey(filter: any, item: any): string {
    if (item == null) return '';
    const vn = filter.value_name || 'VALUE';
    const key = item[vn] ?? item.VALUE ?? item.value
      ?? item.key ?? item.KEY ?? item.mekey ?? item.parnet;
    return key == null ? '' : String(key);
  }

  isMultiChecked(filter: any, opt: any): boolean {
    if (!filter.selected || !_.isArray(filter.selected) || !filter.selected.length) {
      return false;
    }
    const optKey = this.getMultiOptionKey(filter, opt);
    if (!optKey) return false;
    return filter.selected.some(
      (s: any) => this.getMultiOptionKey(filter, s) === optKey
    );
  }

  toggleMultiItem(filter: any, opt: any): void {
    if (!_.isArray(filter.selected)) filter.selected = [];
    const optKey = this.getMultiOptionKey(filter, opt);

    if (this.isSingleSelectDropdown(filter)) {
      const alreadySelected = optKey
        ? filter.selected.some(
            (s: any) => this.getMultiOptionKey(filter, s) === optKey
          )
        : false;
      filter.selected = alreadySelected ? [] : [opt];
    } else {
      const idx = optKey
        ? filter.selected.findIndex(
            (s: any) => this.getMultiOptionKey(filter, s) === optKey
          )
        : -1;
      if (idx >= 0) {
        filter.selected.splice(idx, 1);
      } else {
        filter.selected.push(opt);
      }
    }
    this.cdr.detectChanges();
    this.triggerCascade(filter);
    // Legacy parity: `dfSelectMultiple` / `dfSelectVirtualScroll` /
    // `dfSelectSingle` directives never broadcast `showSaveButton`; only
    // `dfSelect` (single-select) does. Do not call
    // `maybeUpdateImportSaveButtonState` here.
  }

  multiSelectAll(filter: any): void {
    filter.selected = [...filter.values];
    this.cdr.detectChanges();
    this.triggerCascade(filter);
    // Legacy parity: see `toggleMultiItem` comment.
  }

  multiUnselectAll(filter: any): void {
    filter.selected = [];
    this.cdr.detectChanges();
    this.triggerCascade(filter);
    // Legacy parity: see `toggleMultiItem` comment.
  }

  onMultiSearch(_filter: any): void {
    this.cdr.detectChanges();
  }

  openDocument(): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen?.screen_key) return;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '80vw';
    dialogConfig.data = {
      screen_key: activeScreen.screen_key,
      client_key: sessionStorage.getItem('client_key') || '',
      screenName: activeScreen.label || activeScreen.screen_name || '',
      baseUrl: environment.admin_api_url || '/admin',
    };
    this.dialog.open(ScreenDocumentsComponent, dialogConfig);
  }

  launchCustomModalForSpecificScreen(screenId: number): void {
    const callName = DataFiltersComponent.SCREEN_CALLBACK_MAP[screenId];
    if (!callName) return;

    const registryEntry = this.componentRegistry.resolve(callName);
    if (!registryEntry) {
      console.warn(`[DataFilters] No registry entry for callName: ${callName} (screen ${screenId})`);
      return;
    }

    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const filterParams = activeScreen?.filters?.getFilterParams?.() || {};
    const globalParams = { ...(this.globalService.global?.globalParams || {}) };
    globalParams.screen_key = activeScreen?.screen_key || globalParams.screen_key;

    let userSettings: any = {};
    try {
      userSettings = safeJsonInput<any>(sessionStorage.getItem('user_settings') || '{}', null, 'data-filters.parse2');
    } catch (_e) { /* ignore */ }

    const dialogData: CeHostDialogData = {
      ceTagName: registryEntry.ceTagName,
      rowData: {},
      colData: {},
      headerData: {
        globalParams,
        userSettingsData: userSettings,
        serverConfigData: this.appService.SERVER_CONFIG || {},
        generalConfigData: { ...environment },
      },
      baseUrls: {
        api: environment.base_url,
        admin: environment.admin_api_url,
        custom: environment.custom_api_url,
        efile: environment.efile_api_url,
        pdfEngine: environment.pdf_engine_api_url,
        bulkPdfEngine: environment.bulk_pdf_engine_api_url,
      },
      filterParams,
      gridData: {},
      modalName: callName,
    };

    const isEfileCommentsModal =
      (registryEntry.ceTagName || '').toLowerCase().includes('efile-comments-modal');
    const basePanel = registryEntry.dialogPanelClass || 'ce-host-dialog-panel';
    const panelClass = Array.isArray(basePanel)
      ? [...basePanel]
      : [basePanel];
    if (isEfileCommentsModal) {
      panelClass.push('ce-host-dialog-efile-comments');
    }

    const dialogRef = this.dialog.open(CeHostDialogComponent, {
      data: dialogData,
      width: registryEntry.dialogWidth || '90vw',
      height: isEfileCommentsModal ? undefined : registryEntry.dialogHeight || undefined,
      panelClass,
      disableClose: true,
      ...(isEfileCommentsModal ? { position: { top: '48px' } } : {}),
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: any) => {
      if (result?.action === 'refresh') {
        this.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.tabSub) {
      this.tabSub.unsubscribe();
    }
    if (this.downloadDoneSub) {
      this.downloadDoneSub.unsubscribe();
    }
    if (this.globalParamsSub) {
      this.globalParamsSub.unsubscribe();
    }
    if (this.isCeScreenSub) {
      this.isCeScreenSub.unsubscribe();
    }
    if (this.showFilterGroupListenerFn) {
      this.gridEventService.off('showFilterGroup', this.showFilterGroupListenerFn);
    }
    document.removeEventListener('mousedown', this.onDocPointerDownCapture, true);
  }
}
